import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import _ from 'lodash';
import useFetch from 'use-http';
import CreatableSelect from 'react-select/creatable';
import Select, { StylesConfig } from 'react-select';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import Fieldset from 'src/components/project-billing/components/FieldSetComponent';
import CheckRegisterItem from 'src/models/personnel/CheckRegisterItem';
import { ODataOptions, ODataFilter, ODataFilterOperators } from 'src/util/ODataBuilder';

export type CheckRegisterStatus = 'Void' | 'Paid' | 'Pending';
export type CheckRegisterDisbursement = 'C' | 'D';
export type CheckRegisterStubStatus = 'Yes' | 'No';
// Mirrors the Select All/Deselect All/Select Checks/Select Dir. Dep. buttons - moved here from
// CheckRegisterPage.tsx's button row into the search panel, but the actual selection logic
// (operating on the grid's own react-hook-form instance) still lives on the page; this component
// only renders the control and reports the chosen mode back up via onSelectionModeChange.
export type CheckRegisterSelectionMode = 'select' | 'deselect' | 'check' | 'directDeposit';

export interface ICheckRegisterSearchCriteria {
    periodNo?: number;
    tsdNo?: number;
    office?: string;
    paidTo?: string;
    checkNo?: number;
    checkDate?: Date;
    checkType?: CheckRegisterDisbursement;
    amount?: number;
    amountLike?: boolean;
    status?: CheckRegisterStatus;
    // undefined = "All" (no filter); 'Yes'/'No' filters to whether a check_stub row exists for
    // the check (mirrors the "Check Doc." column).
    stubStatus?: CheckRegisterStubStatus;
}

interface ICheckRegisterSearchProps {
    setIsCollapsed?: (isCollapsed: boolean) => void;
    runQuery?: (criteria: ICheckRegisterSearchCriteria) => void;
    checkRegisterItems?: Array<CheckRegisterItem>;
    selectionMode?: CheckRegisterSelectionMode;
    onSelectionModeChange?: (mode: CheckRegisterSelectionMode) => void;
    size?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14;
    // Fetched once by the parent page and handed down here so the Office filter can be a React
    // Select (typeable + filterable by either code or name) without a duplicate fetch.
    officeOptions?: Array<{ code: string; dscr: string }>;
}

interface IOptionType {
    value: string;
    label: string;
}

// Statuses mirror displayStatus() in CheckRegisterPage.tsx (Void takes precedence over Printed).
const STATUS_OPTIONS: Array<{ value: CheckRegisterStatus; label: string }> = [
    { value: 'Void', label: 'Void' },
    { value: 'Paid', label: 'Paid' },
    { value: 'Pending', label: 'Pending' },
];

const DISBURSEMENT_OPTIONS: Array<{ value: CheckRegisterDisbursement; label: string }> = [
    { value: 'C', label: 'Check' },
    { value: 'D', label: 'Direct Deposit' },
];

const STUB_STATUS_OPTIONS: Array<{ value: CheckRegisterStubStatus; label: string }> = [
    { value: 'Yes', label: 'Yes' },
    { value: 'No', label: 'No' },
];

const SELECTION_MODE_OPTIONS: Array<{ mode: CheckRegisterSelectionMode; label: string }> = [
    { mode: 'select', label: 'All' },
    { mode: 'deselect', label: 'None' },
    { mode: 'check', label: 'Check' },
    { mode: 'directDeposit', label: 'Direct Deposit' },
];

export const distinctAmountOptions = (items: Array<CheckRegisterItem>): Array<IOptionType> => {
    const seen = new Set<string>();
    const options: Array<IOptionType> = [];
    (items ?? []).forEach((item) => {
        if (item?.amount == null) {
            return;
        }
        const label = item.amount.toString();
        if (!seen.has(label)) {
            seen.add(label);
            options.push({ value: label, label });
        }
    });
    return options;
};

// Same compact-control override ClientProjectSearch.tsx uses for its react-select fields, plus a
// container/control width override so the Amount field actually renders wider - a plain `style`
// prop on <CreatableSelect> doesn't reach the visible control, only the `styles` config does.
const customStyles: StylesConfig<IOptionType, false> = {
    container: (provided) => ({
        ...provided,
        flexGrow: 1,
        minWidth: '11rem',
    }),
    control: (provided) => ({
        ...provided,
        minHeight: '30px',
        height: '30px',
        padding: '0px',
        width: '100%',
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: '0px 8px',
        height: '30px',
    }),
    input: (provided) => ({
        ...provided,
        margin: '0px',
        padding: '0px',
        whiteSpace: 'nowrap',
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: '30px',
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: '0px',
    }),
    clearIndicator: (provided) => ({
        ...provided,
        padding: '0px',
    }),
};

// Matches the plain RHFInputBox `.field` look (src/styles/pages/_leaseInfo.scss:59-65) - bottom-
// only grey 1px border, no border-radius, 3rem height, no focus ring - rather than the compact
// 30px style customStyles above uses for the Amount field.
const officeSelectStyles: StylesConfig<IOptionType, false> = {
    control: (provided) => ({
        ...provided,
        minHeight: '3rem',
        height: '3rem',
        borderRadius: 0,
        border: 'none',
        borderBottom: '1px solid grey',
        boxShadow: 'none',
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: '3rem',
        padding: '0px 8px',
    }),
    input: (provided) => ({
        ...provided,
        margin: '0px',
        padding: '0px',
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: '3rem',
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: '0px',
    }),
    clearIndicator: (provided) => ({
        ...provided,
        padding: '0px',
    }),
};

// Mirrors CreditCardSearch.tsx/ClientProjectSearch.tsx's query-by-example pattern - typing/
// selecting only updates local state, nothing is fetched until "Search" is clicked. Fields are
// the same ones queryable via Oracle Forms' standard Query menu on the CHECK_REGISTER block:
// PERIOD_NO, TSD_NO, OFFICE, PAID_TO, CHECK_NO, CHECK_DATE, CHECK_TYPE, AMOUNT, VOID/PRINTED.
const CheckRegisterSearch = (props: ICheckRegisterSearchProps) => {
    const { register, errors, setValue, getValues } = useForm();
    const { get } = useFetch();
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const [status, setStatus] = useState<CheckRegisterStatus | undefined>(undefined);
    const [disbursement, setDisbursement] = useState<CheckRegisterDisbursement | undefined>(undefined);
    const [stubStatus, setStubStatus] = useState<CheckRegisterStubStatus | undefined>(undefined);
    const [selectedAmount, setSelectedAmount] = useState<number | undefined>(undefined);
    const [amountInputValue, setAmountInputValue] = useState<string>('');
    const [isAmountMenuOpen, setIsAmountMenuOpen] = useState<boolean>(false);
    const [amountOptions, setAmountOptions] = useState<Array<IOptionType>>(distinctAmountOptions(props?.checkRegisterItems));
    const [selectedOffice, setSelectedOffice] = useState<IOptionType | undefined>(undefined);
    const officeSelectOptions: Array<IOptionType> = (props?.officeOptions ?? []).map((o) => ({ value: o.code, label: o.dscr }));
    // Matches typing against either the code or the name - react-select's default filter only
    // checks label (name), which would hide an office if you typed its code instead.
    const filterOfficeOption = (option: { label: string; value: string }, inputValue: string) => {
        const search = inputValue.toLowerCase();
        return option.label.toLowerCase().includes(search) || option.value.toLowerCase().includes(search);
    };

    // new Date(dateStr) on a date-only "YYYY-MM-DD" string (what <input type="date"> gives us)
    // parses as UTC midnight - formatDateForDisplay then reads it back with local-time getters,
    // which silently rolls it back a day in any timezone behind UTC. Build the Date from its
    // Y/M/D parts directly so it's constructed in local time and round-trips to the same day.
    const toDate = (dateStr: string) => {
        if (!dateStr) {
            return undefined;
        }
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
    };
    const toNumber = (numStr: string) => (!!numStr && !isNaN(Number(numStr)) ? Number(numStr) : undefined);

    // Seeds the Amount dropdown with the distinct totals already on the loaded grid page, same as
    // ClientProjectSearch's "Total $" field seeding from props.invoices.
    useEffect(() => {
        if (!amountInputValue) {
            setAmountOptions(distinctAmountOptions(props?.checkRegisterItems));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.checkRegisterItems]);

    // fetchAmountMatches is re-pointed to a fresh closure every render (so it always sees the
    // latest `get`), while amountSearch itself is created exactly once via lazy useState so its
    // debounce timer actually persists across keystrokes instead of restarting every render.
    const fetchAmountMatchesRef = useRef<(inputText: string) => void>();
    fetchAmountMatchesRef.current = async (inputText: string) => {
        // "Like" unchecked = literal/exact match; checked = contains - same semantics the final
        // Search applies in CheckRegisterPage.tsx's handleSearchQuery.
        const isLike = !!getValues('amountLike');
        const options = new ODataOptions().addSkipTop(0, 50);
        options.addFilter(new ODataFilter('amount', isLike ? ODataFilterOperators.Contains : ODataFilterOperators.Equals, inputText));
        const result = await get(`/v1.0/Payroll${options.toString()}`);
        if (Array.isArray(result?.items)) {
            setAmountOptions(distinctAmountOptions(result.items));
        }
    };
    const [amountSearch] = useState(() => _.debounce((inputText: string) => {
        fetchAmountMatchesRef.current(inputText);
    }, 500));

    const handleAmountLikeChange = () => {
        if (amountInputValue) {
            amountSearch(amountInputValue);
        }
    };

    const handleAmountInputChange = (val: string) => {
        setAmountInputValue(val);
        if (val) {
            amountSearch(val);
        } else {
            setAmountOptions(distinctAmountOptions(props?.checkRegisterItems));
        }
    };

    const executeSearch = () => {
        const values = getValues();
        const criteria: ICheckRegisterSearchCriteria = {
            periodNo: toNumber(values.periodNoSearch),
            tsdNo: toNumber(values.tsdNoSearch),
            office: selectedOffice?.value,
            paidTo: values.paidToSearch || undefined,
            checkNo: toNumber(values.checkNoSearch),
            checkDate: toDate(values.checkDateSearch),
            checkType: disbursement,
            amount: selectedAmount,
            amountLike: !!values.amountLike,
            status,
            stubStatus,
        };

        if (props?.runQuery) {
            props.runQuery(criteria);
        }
    };

    const clearSearchFilters = () => {
        setValue('periodNoSearch', '');
        setValue('tsdNoSearch', '');
        setSelectedOffice(undefined);
        setValue('paidToSearch', '');
        setValue('checkNoSearch', '');
        setValue('checkDateSearch', '');
        setValue('amountLike', false);
        setStatus(undefined);
        setDisbursement(undefined);
        setStubStatus(undefined);
        setSelectedAmount(undefined);
        setAmountInputValue('');
        setAmountOptions(distinctAmountOptions(props?.checkRegisterItems));

        if (props?.runQuery) {
            props.runQuery({});
        }
    };

    const handleSetIsCollapsed = (collapsed: boolean) => {
        setIsCollapsed(collapsed);
        if (props?.setIsCollapsed) {
            props.setIsCollapsed(collapsed);
        }
    };

    if (isCollapsed) {
        return (
            <div style={{ textAlign: 'center' }}>
                <Button
                    text="(+) Search Filters"
                    className="blue-hover-button"
                    style={{ height: '2rem', margin: '0', padding: '.2rem' }}
                    onClick={() => handleSetIsCollapsed(false)}
                />
            </div>
        );
    }

    return (
        <Page style={{ justifyContent: 'center' }}>
            <Page.Section width={!!props?.size ? props.size : 12}>
                <Panel backgroundColor="#D3D3D3">
                    <Panel panelTitle="Check Register Search">
                        <form>
                            <div className="form-row" style={{ padding: '.5rem', margin: '0', flexWrap: 'nowrap', alignItems: 'flex-end' }}>
                                <div style={{ width: '9%' }}>
                                    <Fieldset label="Period No." style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox name="periodNoSearch" type="number" register={register} errors={errors} style={{ width: '100%' }} bodyStyle={{ width: '100%' }} />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '9%', paddingLeft: '4px' }}>
                                    <Fieldset label="DLC No." style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox name="tsdNoSearch" type="number" register={register} errors={errors} style={{ width: '100%' }} bodyStyle={{ width: '100%' }} />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '18%', paddingLeft: '4px' }}>
                                    <Fieldset label="Office" style={{ border: 0, padding: 0 }}>
                                        <Select
                                            isClearable={true}
                                            inputId="checkRegisterOfficeSearchInput"
                                            placeholder="Office"
                                            options={officeSelectOptions}
                                            value={selectedOffice ?? null}
                                            filterOption={filterOfficeOption}
                                            onChange={(val: IOptionType | null) => setSelectedOffice(val ?? undefined)}
                                            isSearchable={true}
                                            styles={officeSelectStyles}
                                        />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '14%', paddingLeft: '4px' }}>
                                    <Fieldset label="Paid To." style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox name="paidToSearch" type="text" register={register} errors={errors} style={{ width: '100%' }} bodyStyle={{ width: '100%' }} />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '10%', paddingLeft: '4px' }}>
                                    <Fieldset label="Check No." style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox name="checkNoSearch" type="number" register={register} errors={errors} style={{ width: '100%' }} bodyStyle={{ width: '100%' }} />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '12%', paddingLeft: '4px' }}>
                                    <Fieldset label="Check Date" style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox name="checkDateSearch" type="date" register={register} errors={errors} style={{ width: '100%' }} bodyStyle={{ width: '100%' }} />
                                    </Fieldset>
                                </div>
                                <div style={{ display: 'flex', marginLeft: 'auto', flexShrink: 0, paddingLeft: '1rem' }}>
                                    <Button
                                        text="Search"
                                        className="blue-hover-button"
                                        style={{ whiteSpace: 'nowrap', width: '7rem', height: '3rem', verticalAlign: 'middle', marginLeft: '1rem', borderRadius: '5px' }}
                                        onClick={() => executeSearch()}
                                    />
                                    <Button
                                        text="(-) Minimize"
                                        className="blue-hover-button"
                                        style={{ whiteSpace: 'nowrap', marginLeft: '1rem', width: '9rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                        onClick={() => handleSetIsCollapsed(true)}
                                    />
                                    <Button
                                        text="Clear"
                                        className="blue-hover-button"
                                        style={{ whiteSpace: 'nowrap', marginLeft: '1rem', width: '7rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                        onClick={() => clearSearchFilters()}
                                    />
                                </div>
                            </div>
                            <div className="form-row" style={{ padding: '.5rem', margin: '0', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                <div style={{ paddingRight: '1rem' }}>
                                    <Fieldset label="Disbursement" style={{ display: 'flex', alignItems: 'center', gap: '.75rem', whiteSpace: 'nowrap' }}>
                                        <span style={{ marginLeft: '.5rem' }}>
                                            <input
                                                type="radio"
                                                name="checkRegisterDisbursementSearch"
                                                id="checkRegisterDisbursementSearch-any"
                                                checked={disbursement === undefined}
                                                onChange={() => setDisbursement(undefined)}
                                            />
                                            <label htmlFor="checkRegisterDisbursementSearch-any" style={{ marginLeft: '.25rem' }}>Any</label>
                                        </span>
                                        {DISBURSEMENT_OPTIONS.map((option) => (
                                            <span key={option.value} style={{ marginLeft: '.5rem' }}>
                                                <input
                                                    type="radio"
                                                    name="checkRegisterDisbursementSearch"
                                                    id={`checkRegisterDisbursementSearch-${option.value}`}
                                                    checked={disbursement === option.value}
                                                    onChange={() => setDisbursement(option.value)}
                                                />
                                                <label htmlFor={`checkRegisterDisbursementSearch-${option.value}`} style={{ marginLeft: '.25rem' }}>{option.label}</label>
                                            </span>
                                        ))}
                                    </Fieldset>
                                </div>
                                <div style={{ width: '13%' }}>
                                    <Fieldset
                                        label="Amount $"
                                        style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', width: '100%', minWidth: '14rem', padding: '2px' }}
                                    >
                                        <label style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '1px', margin: 0 }}>
                                            Like
                                            <input
                                                type="checkbox"
                                                name="amountLike"
                                                ref={register}
                                                value="1"
                                                defaultValue="1"
                                                defaultChecked={false}
                                                onChange={handleAmountLikeChange}
                                            />
                                        </label>
                                        <CreatableSelect
                                            isClearable={true}
                                            className="createable-select-width"
                                            name="amountSearch"
                                            inputId="checkRegisterAmountSearchInput"
                                            value={
                                                selectedAmount != null
                                                    ? { value: selectedAmount.toString(), label: selectedAmount.toString() }
                                                    : amountInputValue
                                                        ? { value: amountInputValue, label: amountInputValue }
                                                        : null
                                            }
                                            placeholder="Amount"
                                            options={amountOptions}
                                            inputValue={amountInputValue}
                                            onInputChange={handleAmountInputChange}
                                            onBlur={() => {
                                                setIsAmountMenuOpen(false);
                                                if (amountInputValue) {
                                                    const num = Number(amountInputValue);
                                                    if (!isNaN(num)) {
                                                        setSelectedAmount(num);
                                                        setAmountInputValue('');
                                                    }
                                                }
                                            }}
                                            onChange={(val: IOptionType | null) => {
                                                if (val) {
                                                    const num = Number(val.label);
                                                    setSelectedAmount(isNaN(num) ? undefined : num);
                                                } else {
                                                    setSelectedAmount(undefined);
                                                }
                                                setAmountInputValue('');
                                            }}
                                            onCreateOption={(val) => {
                                                const num = Number(val);
                                                if (!isNaN(num)) {
                                                    setSelectedAmount(num);
                                                    setAmountInputValue('');
                                                }
                                            }}
                                            onMenuOpen={() => setIsAmountMenuOpen(true)}
                                            onMenuClose={() => setIsAmountMenuOpen(false)}
                                            menuIsOpen={isAmountMenuOpen}
                                            isSearchable={true}
                                            styles={customStyles}
                                        />
                                    </Fieldset>
                                </div>
                                <div style={{ paddingLeft: '4px' }}>
                                    <Fieldset label="Status" style={{ display: 'flex', alignItems: 'center', gap: '.75rem', whiteSpace: 'nowrap' }}>
                                        <span style={{ marginLeft: '.5rem' }}>
                                            <input
                                                type="radio"
                                                name="checkRegisterStatusSearch"
                                                id="checkRegisterStatusSearch-any"
                                                checked={status === undefined}
                                                onChange={() => setStatus(undefined)}
                                            />
                                            <label htmlFor="checkRegisterStatusSearch-any" style={{ marginLeft: '.25rem' }}>Any</label>
                                        </span>
                                        {STATUS_OPTIONS.map((option) => (
                                            <span key={option.value} style={{ marginLeft: '.5rem' }}>
                                                <input
                                                    type="radio"
                                                    name="checkRegisterStatusSearch"
                                                    id={`checkRegisterStatusSearch-${option.value}`}
                                                    checked={status === option.value}
                                                    onChange={() => setStatus(option.value)}
                                                />
                                                <label htmlFor={`checkRegisterStatusSearch-${option.value}`} style={{ marginLeft: '.25rem' }}>{option.label}</label>
                                            </span>
                                        ))}
                                    </Fieldset>
                                </div>
                                <div style={{ paddingLeft: '4px' }}>
                                    <Fieldset label="Saved Stub" style={{ display: 'flex', alignItems: 'center', gap: '.75rem', whiteSpace: 'nowrap' }}>
                                        <span style={{ marginLeft: '.5rem' }}>
                                            <input
                                                type="radio"
                                                name="checkRegisterStubStatusSearch"
                                                id="checkRegisterStubStatusSearch-any"
                                                checked={stubStatus === undefined}
                                                onChange={() => setStubStatus(undefined)}
                                            />
                                            <label htmlFor="checkRegisterStubStatusSearch-any" style={{ marginLeft: '.25rem' }}>All</label>
                                        </span>
                                        {STUB_STATUS_OPTIONS.map((option) => (
                                            <span key={option.value} style={{ marginLeft: '.5rem' }}>
                                                <input
                                                    type="radio"
                                                    name="checkRegisterStubStatusSearch"
                                                    id={`checkRegisterStubStatusSearch-${option.value}`}
                                                    checked={stubStatus === option.value}
                                                    onChange={() => setStubStatus(option.value)}
                                                />
                                                <label htmlFor={`checkRegisterStubStatusSearch-${option.value}`} style={{ marginLeft: '.25rem' }}>{option.label}</label>
                                            </span>
                                        ))}
                                    </Fieldset>
                                </div>
                                <div style={{ paddingLeft: '4px', marginLeft: 'auto' }}>
                                    <Fieldset label="Selection" style={{ display: 'flex', alignItems: 'center', gap: '.75rem', whiteSpace: 'nowrap' }}>
                                        {SELECTION_MODE_OPTIONS.map(({ mode, label }) => (
                                            <span key={mode} style={{ marginLeft: '.5rem' }}>
                                                <input
                                                    type="radio"
                                                    name="checkRegisterSelectionMode"
                                                    id={`checkRegisterSelectionMode-${mode}`}
                                                    checked={props.selectionMode === mode}
                                                    onChange={() => props.onSelectionModeChange && props.onSelectionModeChange(mode)}
                                                />
                                                <label htmlFor={`checkRegisterSelectionMode-${mode}`} style={{ marginLeft: '.25rem' }}>{label}</label>
                                            </span>
                                        ))}
                                    </Fieldset>
                                </div>
                            </div>
                        </form>
                    </Panel>
                </Panel>
            </Page.Section>
        </Page>
    );
};

export default CheckRegisterSearch;



// WEBPACK FOOTER //
// ./src/components/personnel/components/CheckRegisterSearch.tsx